import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, inject, input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Location } from '@angular/common';
import { CommonModule } from '@angular/common';

import { AuthenticationService } from '~/core/services/authentication.service';
import { MenuTopBarComponent } from '~/containers/menu-top-bar/menu-top-bar.component';
import { HelperPlus2Service } from '~/core/services/helper-plus2.service';
import { ThemeService } from '~/core/services/theme.service';
import { CasinoService } from '~/core/services/casino.service';
import { EventService } from '~/core/services/event.service';
import { CasinoCustomizationService } from '~/core/services/casino-customization.service';
import { SmarticoService, SmarticoUserService } from '~/core/services/smartico';
import { GamificationProviderService } from '~/core/services/gamification-provider.service';
import { AuthenticationFieldComponent } from '~/components/authentication-field/authentication-field.component';
import { CasinoUtils } from '~/core/helpers/casino.utils';
import { ThemeEditorService } from '~/core/services/theme-editor.service';
// import { ThemeEditorFloatingComponent } from '~/components/theme-editor-floating/theme-editor-floating.component';
import { HeaderConfigService } from '~/core/services/header-config.service';
import { HeaderActiveItemService } from '~/core/services/header-active-item.service';
import { ItemFieldsService } from '~/core/services/item-fields/item-fields.service';
import { ItemField } from '~/core/services/item-fields/item-fields';
import { HeaderOverflowRowComponent } from '~/components/header-overflow-row/header-overflow-row.component';
import { UtilsService } from '~/core/helpers/utils';
import { resolveCompanyLogo } from '~/core/helpers/company-logo.utils';

@Component({
  selector: 'app-menu-top-bar-plus2',
  templateUrl: './menu-top-bar-plus2.component.html',
  styleUrls: ['./menu-top-bar-plus2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AuthenticationFieldComponent, CommonModule, HeaderOverflowRowComponent],
})
export class MenuTopBarPlus2Component extends MenuTopBarComponent implements OnInit, OnDestroy {
  protected authentication = inject(AuthenticationService);
  helper = inject(HelperPlus2Service);
  location = inject(Location);
  route = inject(ActivatedRoute);
  casinoService = inject(CasinoService);
  themeService = inject(ThemeService);
  eventService = inject(EventService);
  casinoCustomizationService = inject(CasinoCustomizationService);
  smarticoService = inject(SmarticoService);
  smarticoUserService = inject(SmarticoUserService);
  protected gamificationProviderService = inject(GamificationProviderService);
  themeEditorService = inject(ThemeEditorService);
  headerConfigService = inject(HeaderConfigService);
  headerActiveItemService = inject(HeaderActiveItemService);
  itemFieldsService = inject(ItemFieldsService);

  showCrmButton: boolean;
  protected readonly casinoUtils = inject(CasinoUtils);

  readonly desktopMainItems = this.headerConfigService.desktopMainItems;
  readonly desktopSubItems = this.headerConfigService.desktopSubItems;
  readonly desktopMainItemsFlat = this.headerConfigService.desktopMainItemsFlat;
  readonly desktopSubItemsFlat = this.headerConfigService.desktopSubItemsFlat;
  readonly desktopSubPosition = this.headerConfigService.desktopSubPosition;
  readonly hasDesktopSubheader = this.headerConfigService.hasDesktopSubheader;
  hideMenu = computed(() => this.helper.hideMenu());

  constructor() {
    super();
    this.showCrmButton = UtilsService.isBrowser() && this.companyStore.configSignal()?.inbox_page?.xtremepush?.enable;

    this.updateCompanyProperties();

    // Single subscription for company + user + login
    effect(() => {
      this.userStore.userSignal();
      this.refreshMenuAndPermissions();
      this.cdr.markForCheck();
    });
  }

  isLightTheme: boolean;

  readonly hasTransparentBackground = input<boolean>(false);
  isSmarticoReady: boolean = false;
  isGamificationReady: boolean = false;
  public destroy$ = new Subject<void>();

  protected smarticoStateSubscription: Subscription = Subscription.EMPTY;
  protected cdr = inject(ChangeDetectorRef);
  showThemeEditor = false;
  isCasinoOpenedMemo = false;

  ngOnInit() {
    super.ngOnInit();
    this.isLightTheme = ThemeService.isLightTheme;
    this.refreshSecondBarMemos();

    UtilsService.valuesHidden$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  private refreshSecondBarMemos(): void {
    this.isCasinoOpenedMemo = (this.helper?.isCasino ?? false) || (this.helper?.isCasinoLive ?? false);
  }

  private refreshMenuAndPermissions(): void {
    if (!this.userStore.userSignal()) {
      this.auth.resetSessionTime();
    }
    this.showThemeEditor = this.authentication.isMasterUser;
    this.updateCompanyProperties();
  }

  override updateUserProperties() {
    super.updateUserProperties();
    this.refreshSecondBarMemos();
    this.cdr?.markForCheck();
  }

  override setLanguage(event: { id: string }) {
    super.setLanguage(event);
    this.cdr?.markForCheck();
  }

  updateCompanyProperties() {
    super.updateCompanyProperties();
    this.refreshSecondBarMemos();
    this.cdr?.markForCheck();
  }

  onItemClick(item: ItemField): void {
    this.itemFieldsService.openItemFieldAction(item);
  }

  // empty to avoid duplicated requests on /me on system init
  validateAuthenticatedUser() {}

  toggleTheme() {
    ThemeService.toggleLightDarkTheme();
    this.companyLogo = resolveCompanyLogo(this.companyStore.companySignal(), ThemeService.isLightTheme);
    this.isLightTheme = ThemeService.isLightTheme;
    this.cdr?.markForCheck();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.smarticoStateSubscription) {
      this.smarticoStateSubscription.unsubscribe();
    }
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleLoginMenu() {
    this.helper.toggleLoginMenu.emit();
  }

  collapseMenu() {
    this.helper.leftMenuCollapsing.set(!this.helper.leftMenuCollapsing());
  }
}
